import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ClipboardList,
  Plus,
  Trash2,
  X,
  ChevronLeft,
  Loader2,
  Package,
  CheckCircle2,
  Clock,
  User as UserIcon,
  Printer,
  Share2,
  AlertTriangle,
} from "lucide-react";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });

const STATUS_META = {
  open:      { label: "مفتوح — قيد العدّ", color: "#F5A623", icon: Clock },
  submitted: { label: "مُسلَّم من المجهّز", color: "#7ED321", icon: CheckCircle2 },
};

// ─────── New session modal ───────
const NewSessionModal = ({ mujahezList, onClose, onSaved }) => {
  const [mujahezId, setMujahezId] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!mujahezId) { toast.error("اختر المجهّز"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/inventory-counts`, { mujahez_id: mujahezId, title });
      toast.success("تم إنشاء جلسة جرد جديدة");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-3" dir="rtl"
      data-testid="inventory-new-modal" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-md p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center pb-3 mb-3 border-b-2 border-[#1E3A5F]/20">
          <h3 className="font-bold text-lg text-[#1E3A5F] flex items-center gap-2">
            <ClipboardList className="w-5 h-5" /> جلسة جرد جديدة
          </h3>
          <button onClick={onClose} data-testid="inventory-new-close"><X className="w-5 h-5" /></button>
        </div>

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">المجهّز المسؤول عن الجرد</label>
        <select
          value={mujahezId}
          onChange={(e) => setMujahezId(e.target.value)}
          data-testid="inventory-mujahez-select"
          className="w-full px-3 py-2 rounded-xl border-2 border-[#3B82F6] mb-3"
        >
          <option value="">— اختر المجهّز —</option>
          {mujahezList.map((m) => (
            <option key={m.id || m.mujahez_id} value={m.id || m.mujahez_id}>
              {m.full_name} {m.phone ? `(${m.phone})` : ""}
            </option>
          ))}
        </select>

        <label className="text-xs font-bold text-[#1E3A5F] mb-1 block">عنوان الجلسة (اختياري)</label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="مثال: جرد نهاية الشهر"
          data-testid="inventory-title-input"
          className="w-full px-3 py-2 rounded-xl border border-[#E4EBE6] mb-4"
        />

        <button
          onClick={submit} disabled={busy}
          data-testid="inventory-new-submit"
          className="w-full bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold py-2 rounded-full"
        >
          {busy ? "جاري الإنشاء..." : "إنشاء الجلسة"}
        </button>
        <p className="text-[10px] text-[#5C6E64] mt-2 text-center">
          المجهّز سيرى صور المواد وأسماءها فقط — بدون كميات أو أسعار.
        </p>
      </div>
    </div>
  );
};

// ─────── Detail view (manager-side comparison) ───────
const SessionDetail = ({ session, onClose, onChanged }) => {
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOne = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/inventory-counts/${session.id}`);
      setFull(data);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchOne(); }, [session.id]);

  const remove = async () => {
    if (!(await confirmDialog("حذف جلسة الجرد كاملة؟"))) return;
    try {
      await axios.delete(`${API}/admin/inventory-counts/${session.id}`);
      toast.success("تم الحذف");
      onChanged && onChanged();
      onClose();
    } catch { toast.error("فشل"); }
  };

  const summary = useMemo(() => {
    const items = full?.items_enriched || [];
    let positive = 0, negative = 0, equal = 0;
    items.forEach((it) => {
      if (it.variance > 0) positive++;
      else if (it.variance < 0) negative++;
      else equal++;
    });
    return { positive, negative, equal, total: items.length };
  }, [full]);

  const handlePrint = () => {
    if (!full?.items_enriched?.length) { toast.error("لا توجد بنود للطباعة"); return; }
    const w = window.open("", "_blank", "width=820,height=900");
    if (!w) { toast.error("فعّل النوافذ المنبثقة"); return; }
    const rows = full.items_enriched.map((it, i) => `
      <tr>
        <td style="text-align:center">${i + 1}</td>
        <td>${it.name_ar}</td>
        <td style="text-align:center" dir="ltr">${it.system_stock}</td>
        <td style="text-align:center" dir="ltr"><b>${it.counted_qty}</b></td>
        <td style="text-align:center;color:${it.variance === 0 ? "#15803d" : it.variance > 0 ? "#1E3A5F" : "#E85D45"};font-weight:800" dir="ltr">${it.variance > 0 ? "+" : ""}${it.variance}</td>
      </tr>`).join("");
    w.document.write(`<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8"><title>تقرير جرد المخزن - جملتي</title>
<style>
@page { size: A4; margin: 12mm }
* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-family: 'Tajawal', system-ui, sans-serif; margin: 0; padding: 16px; color: #111; direction: rtl; }
h1 { color: #1E3A5F; margin: 0 0 4px; font-size: 22px; }
.sub { color: #5C6E64; font-size: 11px; margin-bottom: 12px; }
table { width: 100%; border-collapse: collapse; }
th, td { border: 1px solid #E4EBE6; padding: 8px; text-align: right; font-size: 13px; }
th { background: #1E3A5F; color: white; }
.footer { margin-top: 18px; font-size: 11px; color: #5C6E64; text-align: center; }
</style></head><body>
<h1>📦 تقرير جرد المخزن — جملتي</h1>
<div class="sub">
  العنوان: <b>${full.title || "—"}</b><br/>
  المجهّز: <b>${full.mujahez_name || "—"}</b><br/>
  التاريخ: <span dir="ltr">${(full.created_at || "").slice(0, 10)}</span> · عدد الأصناف المعدودة: <span dir="ltr">${full.items_enriched.length}</span>
</div>
<table>
  <thead><tr>
    <th style="width:36px">#</th><th>المادة</th>
    <th style="width:110px">المخزن (النظام)</th>
    <th style="width:110px">العدّ الفعلي</th>
    <th style="width:90px">الفرق</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
<div class="footer">شكراً — جملتي · jumlaty.online</div>
</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  const handleWhatsApp = () => {
    if (!full?.items_enriched?.length) { toast.error("لا توجد بنود"); return; }
    const lines = [];
    lines.push("📦 تقرير جرد المخزن — جملتي");
    lines.push(`العنوان: ${full.title || "—"}`);
    lines.push(`المجهّز: ${full.mujahez_name || "—"}`);
    lines.push(`التاريخ: ${(full.created_at || "").slice(0, 10)}`);
    lines.push("─".repeat(20));
    full.items_enriched.forEach((it, i) => {
      const sign = it.variance > 0 ? "+" : "";
      lines.push(`${i + 1}. ${it.name_ar}\n   نظام: ${it.system_stock} | عدّ: ${it.counted_qty} | فرق: ${sign}${it.variance}`);
    });
    lines.push("─".repeat(20));
    lines.push(`عدد الأصناف: ${full.items_enriched.length}`);
    lines.push("https://jumlaty.online");
    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  if (loading || !full) {
    return (
      <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-3" dir="rtl">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const sm = STATUS_META[full.status] || STATUS_META.open;
  const StatusIcon = sm.icon;

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-2" dir="rtl"
      data-testid="inventory-detail-modal" onClick={onClose}>
      <div className="bg-[#F8FAFC] rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(ev) => ev.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white p-4 flex justify-between items-start sticky top-0 z-10">
          <div className="min-w-0">
            <h3 className="font-bold text-xl truncate">{full.title || "جرد"}</h3>
            <p className="text-xs opacity-90 flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> {full.mujahez_name || "—"}
              <span className="mx-1">·</span>
              <span dir="ltr">{(full.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </p>
            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
              style={{ backgroundColor: `${sm.color}40`, color: "#fff" }}>
              <StatusIcon className="w-3 h-3" /> {sm.label}
            </span>
          </div>
          <button onClick={onClose} data-testid="inventory-detail-close"
            className="bg-white/20 hover:bg-white/30 rounded-full p-1.5"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-3 space-y-3">
          {/* Summary cards */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-white rounded-xl p-3 text-center border border-[#E4EBE6]">
              <p className="text-[10px] text-[#5C6E64]">عدد البنود</p>
              <p className="font-black text-xl text-[#1E3A5F]" dir="ltr">{summary.total}</p>
            </div>
            <div className="bg-[#ECFDF5] rounded-xl p-3 text-center border border-[#7ED321]">
              <p className="text-[10px] text-[#15803d]">متطابق</p>
              <p className="font-black text-xl text-[#15803d]" dir="ltr">{summary.equal}</p>
            </div>
            <div className="bg-[#F0F9FF] rounded-xl p-3 text-center border border-[#3B82F6]">
              <p className="text-[10px] text-[#1E3A5F]">زيادة (+)</p>
              <p className="font-black text-xl text-[#1E3A5F]" dir="ltr">{summary.positive}</p>
            </div>
            <div className="bg-[#FEF2F2] rounded-xl p-3 text-center border border-[#E85D45]">
              <p className="text-[10px] text-[#E85D45]">نقص (−)</p>
              <p className="font-black text-xl text-[#E85D45]" dir="ltr">{summary.negative}</p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handlePrint}
              data-testid="inventory-print-btn"
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
            >
              <Printer className="w-4 h-4" /> طباعة / PDF
            </button>
            <button
              onClick={handleWhatsApp}
              data-testid="inventory-whatsapp-btn"
              className="bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
            >
              <Share2 className="w-4 h-4" /> مشاركة على واتساب
            </button>
            <button
              onClick={remove}
              data-testid="inventory-delete-btn"
              className="bg-[#E85D45] hover:bg-[#d54d35] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1 mr-auto"
            >
              <Trash2 className="w-4 h-4" /> حذف الجلسة
            </button>
          </div>

          {full.status === "open" && (
            <div className="bg-[#FFF8E1] border-2 border-[#F5A623] rounded-xl p-3 flex items-center gap-2"
              data-testid="inventory-open-note">
              <AlertTriangle className="w-5 h-5 text-[#F5A623] shrink-0" />
              <p className="text-xs text-[#a06e10]">
                لم يُسلّم المجهّز الجرد بعد — البيانات الظاهرة جزئية وتتحدّث مع كل إدخال جديد.
              </p>
            </div>
          )}

          {/* Items table */}
          <div className="bg-white rounded-2xl border border-[#E4EBE6] overflow-hidden">
            <div className="bg-[#1E3A5F] text-white px-3 py-2 text-xs font-bold flex items-center gap-1">
              <Package className="w-4 h-4" /> البنود المعدودة ({summary.total})
            </div>
            {summary.total === 0 ? (
              <p className="text-center py-8 text-[#5C6E64] text-sm">لم يُدخل المجهّز أي عدّ بعد</p>
            ) : (
              <div className="divide-y divide-[#E4EBE6]">
                {full.items_enriched.map((it, i) => {
                  const v = it.variance;
                  const vColor = v === 0 ? "#15803d" : v > 0 ? "#1E3A5F" : "#E85D45";
                  return (
                    <div key={it.product_id} data-testid={`inventory-row-${it.product_id}`}
                      className="flex items-center gap-2 p-2.5">
                      <span className="bg-[#F3F6F4] text-[#5C6E64] rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold" dir="ltr">{i + 1}</span>
                      {it.image_path ? (
                        <img src={`${API}/files/${it.image_path}`} alt="" className="w-12 h-12 rounded-lg object-cover border" />
                      ) : (
                        <div className="w-12 h-12 bg-[#F3F6F4] rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-[#5C6E64]" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#111814] truncate">{it.name_ar}</p>
                        <p className="text-[10px] text-[#5C6E64]">
                          نظام: <span dir="ltr">{it.system_stock}</span>
                          {" · "}عدّ المجهّز: <span dir="ltr" className="font-bold">{it.counted_qty}</span>
                        </p>
                      </div>
                      <div className="text-left">
                        <p className="text-[9px] text-[#5C6E64]">الفرق</p>
                        <p className="font-black text-lg" style={{ color: vColor }} dir="ltr">
                          {v > 0 ? "+" : ""}{v}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────── Main tab ───────
const InventoryCountTab = () => {
  const [sessions, setSessions] = useState([]);
  const [mujahezList, setMujahezList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [openSession, setOpenSession] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sRes, mRes] = await Promise.all([
        axios.get(`${API}/admin/inventory-counts`),
        axios.get(`${API}/admin/mujahez`).catch(() => ({ data: [] })),
      ]);
      setSessions(sRes.data || []);
      setMujahezList(mRes.data || []);
    } catch { toast.error("فشل التحميل"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  if (loading) {
    return <div className="text-center py-10"><Loader2 className="w-8 h-8 animate-spin inline text-[#1E3A5F]" /></div>;
  }

  return (
    <div className="space-y-3" dir="rtl" data-testid="inventory-count-tab">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <ClipboardList className="w-5 h-5" />
          <h3 className="font-bold text-lg">جرد المخزن</h3>
        </div>
        <p className="text-xs opacity-90">
          أنشئ جلسة جرد واختر المجهّز المسؤول. سيرى المجهّز صور المواد وأسماءها فقط (بدون أسعار أو كميات) ويُدخل العدّ الفعلي،
          ثم تظهر هنا المقارنة مع المخزون.
        </p>
      </div>

      <div className="flex justify-between gap-2 flex-wrap">
        <button
          onClick={() => setShowNew(true)}
          data-testid="inventory-create-btn"
          className="bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold py-2 px-4 rounded-full flex items-center gap-1"
        >
          <Plus className="w-4 h-4" /> جلسة جرد جديدة
        </button>
        <span className="text-xs text-[#5C6E64] self-center">
          إجمالي الجلسات: <span className="font-bold text-[#1E3A5F]" dir="ltr">{sessions.length}</span>
        </span>
      </div>

      {sessions.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-8 text-center">
          <ClipboardList className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">لا توجد جلسات جرد بعد</p>
          <p className="text-[10px] text-[#5C6E64] mt-1">اضغط "جلسة جرد جديدة" لبدء أول جرد</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sessions.map((s) => {
            const sm = STATUS_META[s.status] || STATUS_META.open;
            const SI = sm.icon;
            return (
              <button
                key={s.id}
                onClick={() => setOpenSession(s)}
                data-testid={`inventory-card-${s.id}`}
                className="text-right bg-white rounded-2xl p-3 border-2 border-[#E4EBE6] hover:border-[#3B82F6] shadow-sm transition-colors"
              >
                <div className="flex items-center justify-between mb-1.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                    style={{ backgroundColor: `${sm.color}22`, color: sm.color }}>
                    <SI className="w-3 h-3" /> {sm.label}
                  </span>
                  <ChevronLeft className="w-4 h-4 text-[#5C6E64]" />
                </div>
                <p className="font-bold text-[#111814] truncate">{s.title || "جرد"}</p>
                <p className="text-[11px] text-[#5C6E64] mt-0.5 flex items-center gap-1">
                  <UserIcon className="w-3 h-3" /> {s.mujahez_name || "—"}
                </p>
                <div className="flex items-center justify-between mt-2 text-[10px] text-[#5C6E64]">
                  <span dir="ltr">{(s.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  <span className="bg-[#1E3A5F]/10 text-[#1E3A5F] rounded-full px-2 py-0.5 font-bold" dir="ltr">
                    {(s.items || []).length} بند
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {showNew && (
        <NewSessionModal
          mujahezList={mujahezList}
          onClose={() => setShowNew(false)}
          onSaved={fetchAll}
        />
      )}
      {openSession && (
        <SessionDetail
          session={openSession}
          onClose={() => setOpenSession(null)}
          onChanged={fetchAll}
        />
      )}
    </div>
  );
};

export default InventoryCountTab;
